import { useState } from "react"
import { router } from "@inertiajs/react"
import { BellRing, BookOpen, Link2, MailCheck, MoreHorizontal, Trash2 } from "lucide-react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

// The four funnel stages a coach cares about, in order. Kept in lockstep with
// MemberStoryInvite#status — that method is the source of truth, this is only
// how each stage reads.
//
// `sent` is the one stage that reads off a SECOND axis. Interview progress and
// delivery are orthogonal: `status === "sent"` honestly means "the member
// hasn't started", and it says nothing about whether the coach sent them
// anything. Only `markedSent` does. Amber in this table means "waiting on
// someone" — a link the coach has not sent is waiting on the COACH, so it is
// zinc until they send it.
const STAGES = {
  sent: { label: "Invited", className: "bg-amber-100 text-amber-800 border-amber-200" },
  started: { label: "In progress", className: "bg-sky-100 text-sky-800 border-sky-200" },
  draft_ready: { label: "Draft ready", className: "bg-violet-100 text-violet-800 border-violet-200" },
  sent_to_coach: { label: "With you", className: "bg-emerald-100 text-emerald-800 border-emerald-200" },
}

const LINK_CREATED = {
  label: "Link created",
  className: "bg-zinc-100 text-zinc-700 border-zinc-200",
}

/**
 * One roster row's Member Story state: get a link to send, mark one as sent,
 * remove a mis-click, nudge a member who has gone quiet, or open the draft that
 * came back.
 */
export function MemberStoryInviteCell({ user }) {
  const invite = user.memberStoryInvite

  // One in-flight flag for the whole cell. Without it a double-click fires two
  // POSTs and mints twice. Same shape as AddMemberDialog's form.processing.
  const [processing, setProcessing] = useState(false)
  const [confirmingNudge, setConfirmingNudge] = useState(false)

  const post = (path) =>
    router.post(path, { account_id: user.id }, {
      preserveScroll: true,
      onStart: () => setProcessing(true),
      onFinish: () => setProcessing(false),
    })

  // Creating an invite does not email anyone — it mints the link and hands it
  // back for the coach to send themselves (the flash drives the dialog on the
  // page). Same shape as the prospect intake invite.
  const mintLink = () => post("/app/coach/member_story_invites")
  const markSent = () => post("/app/coach/member_story_invites/mark_sent")
  const discard = () => post("/app/coach/member_story_invites/discard")
  const remind = () => post("/app/coach/member_story_invites/remind")

  if (!invite) {
    // Visible text, not a bare glyph. An unlabelled chain icon is half of why
    // one click read as "you invited me".
    return (
      <Button
        variant="ghost"
        size="sm"
        className="text-zinc-500 hover:text-kf-red"
        title="Get a story link to send them — nothing is sent"
        disabled={processing}
        onClick={mintLink}
      >
        <Link2 className="mr-1 h-4 w-4" /> Get link
      </Button>
    )
  }

  if (invite.status === "sent_to_coach" && invite.articleId) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="text-emerald-700 hover:text-emerald-800"
        title="Open their story draft"
        disabled={processing}
        onClick={() => router.visit(`/app/coach/articles/${invite.articleId}`)}
      >
        <BookOpen className="h-4 w-4" />
        <span className="sr-only">Open {user.name}'s story draft</span>
      </Button>
    )
  }

  const stage =
    invite.status === "sent" && !invite.markedSent
      ? LINK_CREATED
      : STAGES[invite.status] || STAGES.sent

  // Available right up until the draft exists: the stalled-halfway member is
  // `started`, not `sent`.
  const inFlight = invite.status === "sent" || invite.status === "started"

  // Narrower than the server's `discardable?` on purpose — an accessed-but-not-
  // started invite is still `sent`, and the server refuses it with a flash.
  // Hiding too little is the unsafe direction; hiding too much is not.
  const canDiscard = invite.status === "sent" && !invite.markedSent

  return (
    <div className="flex items-center justify-end gap-1">
      <Badge variant="secondary" className={`hidden text-xs lg:inline-flex ${stage.className}`}>
        {stage.label}
      </Badge>

      {/* Behind a menu, with real text labels. Four one-click icon buttons in a
          cell this narrow is the fat-finger site the report is about; opening a
          menu and choosing a named item is the two-step interaction. */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="text-zinc-500 hover:text-kf-red" disabled={processing}>
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">Member Story actions for {user.name}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {inFlight && (
            <DropdownMenuItem onClick={mintLink}>
              <Link2 className="mr-2 h-4 w-4" /> Get their link again
            </DropdownMenuItem>
          )}
          {inFlight && !invite.markedSent && (
            <DropdownMenuItem onClick={markSent}>
              <MailCheck className="mr-2 h-4 w-4" /> Mark as sent
            </DropdownMenuItem>
          )}
          {/* The nudge is the only action here that reaches a real person's
              inbox and cannot be taken back, so it confirms first. */}
          {inFlight && invite.markedSent && (
            <DropdownMenuItem onClick={() => setConfirmingNudge(true)}>
              <BellRing className="mr-2 h-4 w-4" /> Send a reminder
            </DropdownMenuItem>
          )}
          {canDiscard && (
            <DropdownMenuItem onClick={discard} className="text-red-600 focus:text-red-600">
              <Trash2 className="mr-2 h-4 w-4" /> Remove this invite
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirmingNudge} onOpenChange={setConfirmingNudge}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send {user.name} a reminder?</AlertDialogTitle>
            <AlertDialogDescription>
              This sends them an email from Kingdom Factor about their story
              interview. It goes to their inbox straight away and can't be taken
              back.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remind}>Send reminder</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
